import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AccountBalance, FinancialAccountBalances } from '@shared/models';
import { fromAggregatedAccountBalance } from '@shared/store';

import { FinancialAccountBalancesComponent } from '../../financial-account/financial-account-balances/financial-account-balances.component';

export const CountryCodes: Record<string, string> = {
  USA: 'USD',
  AZ: 'AZN',
};

@Component({
  selector: 'app-business-account-balance',
  templateUrl: './business-account-balance.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FinancialAccountBalancesComponent],
})
export class BusinessAccountBalanceComponent implements OnInit, OnDestroy {
  @Input() isFullWidth = false;

  @Input() noBottomOffset = false;

  isLoading = true;

  accountBalanceValues!: AccountBalance | undefined;

  selectedCurrency!: string | undefined;

  private destroy$ = new Subject<void>();
  private readonly store = inject(Store);

  private aggregatedBalances: FinancialAccountBalances | null = null;

  ngOnInit() {
    this.isLoading = true;

    this.store
      .select(fromAggregatedAccountBalance.selectAggregatedAccountBalanceDetails)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (balances) => {
          this.aggregatedBalances = balances;
          this.isLoading = false;
          if (balances) {
            this.calculateBalances();
          }
        },
      });

    this.store
      .select(fromAggregatedAccountBalance.selectActiveAggregatedAccountBalanceCurrency)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (currency) => {
          this.selectedCurrency = currency;
          if (this.aggregatedBalances) {
            this.calculateBalances();
          }
        },
      });
  }

  private calculateBalances() {
    if (!this.aggregatedBalances) {
      return;
    }

    // Use USD balances from backend directly
    const usdBalances = this.aggregatedBalances['USD'];

    if (usdBalances) {
      this.accountBalanceValues = {
        accountBalance: usdBalances.accountBalance ?? 0,
        availableBalance: usdBalances.availableBalance ?? 0,
        pendingDeposit: usdBalances.pendingDeposit ?? 0,
        pendingWithdraw: usdBalances.pendingWithdraw ?? 0,
      };
    } else {
      this.accountBalanceValues = {
        accountBalance: 0,
        availableBalance: 0,
        pendingDeposit: 0,
        pendingWithdraw: 0,
      };
    }

    this.selectedCurrency = 'USD';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
